import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Users, Plus, Edit, UserX, UserCheck, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

const ROLE_COLORS = {
    'ADMIN': 'text-red-500 bg-red-500/10 border-red-500/20',
    'WAREHOUSE_STAFF': 'text-blue-500 bg-blue-500/10 border-blue-500/20',
    'ORDER_CONFIRMATION': 'text-amber-500 bg-amber-500/10 border-amber-500/20',
    'ACCOUNTANT': 'text-green-500 bg-green-500/10 border-green-500/20',
    'TYPE_A': 'text-purple-500 bg-purple-500/10 border-purple-500/20',
};

export function UsersPage() {
    const { t, language } = useLanguage();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'WAREHOUSE_STAFF' });
    const [processing, setProcessing] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_URL}/users`, { headers });
            setUsers(response.data.data);
        } catch (error) {
            console.error('Failed to fetch users:', error);
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setEditingUser(null);
        setFormData({ name: '', email: '', password: '', role: 'WAREHOUSE_STAFF' });
        setDialogOpen(true);
    };

    const openEditDialog = (user) => {
        setEditingUser(user);
        setFormData({ name: user.name, email: user.email, password: '', role: user.role });
        setDialogOpen(true);
    };

    const closeDialog = () => {
        setDialogOpen(false);
        setEditingUser(null);
        setFormData({ name: '', email: '', password: '', role: 'WAREHOUSE_STAFF' });
    };

    const handleSubmit = async () => {
        setProcessing(true);
        try {
            if (editingUser) {
                // Update user
                const updateData = {
                    name: formData.name,
                    role: formData.role
                };
                if (formData.password) {
                    updateData.password = formData.password;
                }
                await axios.put(`${API_URL}/users/${editingUser.id}`, updateData, { headers });
            } else {
                // Create user
                await axios.post(`${API_URL}/users`, formData, { headers });
            }
            closeDialog();
            fetchUsers();
        } catch (error) {
            console.error('Failed to save user:', error);
            toast.error(error.response?.data?.error || t('errorSaveUser') || 'Failed to save user');
        } finally {
            setProcessing(false);
        }
    };

    const toggleUserStatus = async (user) => {
        try {
            await axios.put(`${API_URL}/users/${user.id}`, {
                isActive: !user.isActive
            }, { headers });
            fetchUsers();
        } catch (error) {
            console.error('Failed to toggle user status:', error);
            toast.error(error.response?.data?.error || t('errorOccurred'));
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'ADMIN': return t('admin');
            case 'WAREHOUSE_STAFF': return t('warehouseStaff');
            case 'ORDER_CONFIRMATION': return t('orderConfirmation');
            case 'ACCOUNTANT': return t('accountant');
            case 'TYPE_A': return t('typeA') || 'Type A';
            default: return role;
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    const availableRoles = [
        { value: 'ADMIN', label: t('admin') },
        { value: 'WAREHOUSE_STAFF', label: t('warehouseStaff') },
        { value: 'ORDER_CONFIRMATION', label: t('orderConfirmation') },
        { value: 'ACCOUNTANT', label: t('accountant') },
        { value: 'TYPE_A', label: t('typeA') || 'Type A' },
    ];

    return (
        <Layout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold">{t('userManagement')}</h1>
                        <p className="text-muted-foreground">{t('userManagementSubtitle')}</p>
                    </div>
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={fetchUsers}>
                            <RefreshCw className="h-4 w-4 mr-2" />
                            {t('refresh')}
                        </Button>
                        <Button onClick={openCreateDialog}>
                            <Plus className="h-4 w-4 mr-2" />
                            {t('addUser')}
                        </Button>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>{t('totalUsers')}</CardDescription>
                            <CardTitle className="text-3xl">{users.length}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>{t('active')}</CardDescription>
                            <CardTitle className="text-3xl text-green-500">{users.filter(u => u.isActive).length}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>{t('inactive')}</CardDescription>
                            <CardTitle className="text-3xl text-muted-foreground">{users.filter(u => !u.isActive).length}</CardTitle>
                        </CardHeader>
                    </Card>
                    <Card>
                        <CardHeader className="pb-2">
                            <CardDescription>{t('admins')}</CardDescription>
                            <CardTitle className="text-3xl text-red-500">{users.filter(u => u.role === 'ADMIN').length}</CardTitle>
                        </CardHeader>
                    </Card>
                </div>

                {/* Users Table */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            {t('allUsers')}
                        </CardTitle>
                        <CardDescription>{t('staffAccounts')}</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>{t('name')}</TableHead>
                                    <TableHead>{t('email')}</TableHead>
                                    <TableHead>{t('role')}</TableHead>
                                    <TableHead>{t('status')}</TableHead>
                                    <TableHead>{t('created')}</TableHead>
                                    <TableHead className="text-right">{t('actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {users.map((user) => {
                                    return (
                                        <TableRow key={user.id} className={!user.isActive ? 'opacity-50' : ''}>
                                            <TableCell className="font-medium">{user.name}</TableCell>
                                            <TableCell className="text-muted-foreground">{user.email}</TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-1 rounded-full text-xs font-medium border ${ROLE_COLORS[user.role]}`}>
                                                    {getRoleLabel(user.role)}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {user.isActive ? (
                                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-500 border border-green-500/20">
                                                        {t('active')}
                                                    </span>
                                                ) : (
                                                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground border border-border">
                                                        {t('inactive')}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-sm text-muted-foreground">
                                                {formatDate(user.createdAt)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openEditDialog(user)}
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className={user.isActive ? 'text-red-600 border-red-600' : 'text-green-600 border-green-600'}
                                                        onClick={() => toggleUserStatus(user)}
                                                    >
                                                        {user.isActive ? <UserX className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>

                {/* Create/Edit Dialog */}
                <Dialog open={dialogOpen} onOpenChange={closeDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>{editingUser ? t('editUser') : t('addUser')}</DialogTitle>
                            <DialogDescription>
                                {editingUser ? t('updateUserDesc') || 'Update user details below.' : t('createUserDesc') || 'Add a new staff member to the system.'}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">{t('fullName')}</Label>
                                <Input
                                    id="name"
                                    placeholder="John Doe"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="password">{editingUser ? t('newPassword') : t('password')}</Label>
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder={editingUser ? t('leaveBlankToKeep') : "********"}
                                    value={formData.password}
                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                />
                            </div>
                            {!editingUser && (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="email">{t('email')}</Label>
                                        <Input
                                            id="email"
                                            type="email"
                                            placeholder="john@example.com"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        />
                                    </div>
                                </>
                            )}
                            <div className="space-y-2">
                                <Label>{t('role')}</Label>
                                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {availableRoles.map((role) => (
                                            <SelectItem key={role.value} value={role.value}>
                                                {role.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={closeDialog} disabled={processing}>
                                {t('cancel')}
                            </Button>
                            <Button onClick={handleSubmit} disabled={processing}>
                                {processing ? t('loading') : (editingUser ? t('saveChanges') : t('createUser'))}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
